import React, { useContext, useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { Alert, App, Button, Divider } from 'antd'
import { PageHeader } from '../../design-system/PageHeader'
import { ExclamationCircleOutlined, CommentOutlined } from '@ant-design/icons'
import { useManualQuery } from 'graphql-hooks'
import { useIntl } from 'react-intl'
import type { IntlShape } from 'react-intl'

import Api from '@vacationtracker/shared/services/api'
import { getAutomations } from '../../graphql/custom-queries'
import IntlMessages from '../../util/IntlMessages'
import { notificationStore } from '../../context/notificationsContext/store'
import { FeedbackModal } from '@vacationtracker/shared/components/feedback-modal'
import ApiKeysTable, { IApiKeysTableItem } from '../public-api/api-keys-table'
import LegacyApiKeysTable from '../public-api/legacy-api-keys-table'
import { publicApiPaths } from '../public-api/paths'
import { formatRevocation } from '../public-api/revocation-message'
import { PublicApiKeyListItem, useApiKeys } from '../../services/api/public-api-keys-hooks'

import { IAutomations, IGetAutomations } from '../../types/custom-queries'
import { FrontendUrls } from '../../types/urls'
import { IAutomationViewPage } from '@vacationtracker/shared/types/automations'
import { AddonsStatusEnum } from '@vacationtracker/shared/types/addons'
import HandleSubscribeAutomations from '../handle-subscribe-automations'

const DOCUMENTATION_URL = 'https://vacationtracker.io/developers/api-v2'
const MIGRATION_GUIDE_URL = 'https://vacationtracker.io/developers/api-v2/migration'

const toTableItem = (key: PublicApiKeyListItem, intl: IntlShape): IApiKeysTableItem => {
  const ownerName = key.ownerName ?? key.ownerId
  return {
    id: key.keyId,
    name: key.name,
    integrationName: key.integrationName,
    prefix: key.keyPrefix,
    ownerName,
    scopes: key.scopes,
    lastUsedAt: key.lastUsedAt,
    expiresAt: key.expiresAt ?? undefined,
    status: key.status,
    revocationReasonText: key.revocation ? formatRevocation(intl, key.revocation, ownerName) : undefined,
  }
}

// The API needs no add-on any more; only companies still paying for the old one get the control to cancel it.
const PAID_ADDON_STATUSES: string[] = [AddonsStatusEnum.ACTIVE, AddonsStatusEnum.CANCEL_AT_PERIOD_END]

type OpenApiViewProps = Pick<IAutomationViewPage, 'formatMessage' | 'amIAdmin' | 'deleteAutomation' | 'handleSubscribe' | 'status' | 'addonDetails' | 'currentPlan'>

const OpenApiView = ({ formatMessage, amIAdmin, deleteAutomation, handleSubscribe, status, addonDetails, currentPlan }: OpenApiViewProps) => {
  const { modal, notification } = App.useApp()
  const history = useHistory()
  const intl = useIntl()
  const { actionNotifications } = useContext(notificationStore)

  const [automations, setAutomations] = useState<IAutomations[]>([])
  const [deleteAutomationId, setDeleteAutomationId] = useState<string | null>(null)
  const [showFeedbackForm, setShowFeedbackForm] = useState(false)

  const [getAutomationsQuery] = useManualQuery<IGetAutomations>(getAutomations)
  const { data: apiKeys, isLoading: apiKeysLoading } = useApiKeys()

  useEffect(() => {
    // Clears the v1 secret an older version of this page stored for a Swagger UI that no longer exists.
    localStorage.removeItem('authorized')
  }, [])

  useEffect(() => {
    if (amIAdmin) {
      fetchData()
    }
  }, [actionNotifications])

  const fetchData = async () => {
    const response = await getAutomationsQuery()
    if (!response.data || response.error) throw response.error
    setAutomations(response.data.getAutomations.filter((automation) => automation.automationType === 'OPEN_API'))
    setDeleteAutomationId(null)
  }

  const sendFeedbackContactForm = async (value) => {
    try {
      await Api.post('/core/event', {
        ...value,
        eventGroup: 'FEEDBACK',
        eventType: 'AUTOMATION_FEEDBACK',
      })
      setShowFeedbackForm(false)
      notification.success({
        message: formatMessage({ id: 'automation.feedback.thankYou' }),
        icon: <CommentOutlined style={{ color: '#7f00ff' }} />,
      })
    } catch (error) {
      const errorDescription = error.response?.data?.message ? error.response?.data.message : error.message ? error.message : JSON.stringify(error)
      notification.error({
        message: formatMessage({ id: 'error.generic' }),
        description: errorDescription,
        duration: 0,
      })
    }
  }

  const showConfirmDelete = (row: IAutomations) => {
    modal.confirm({
      title: formatMessage({ id: 'addons.openApi.notificationDeleteTitle' }),
      icon: <ExclamationCircleOutlined />,
      content: formatMessage({ id: 'addons.openApi.notificationDeleteConfirm' }, { name: row.name, strong: (...chunks) => <strong>{chunks}</strong> }),
      okText: formatMessage({ id: 'app.delete' }),
      okType: 'danger',
      width: 500,
      maskClosable: true,
      onOk() {
        setDeleteAutomationId(row.id)
        deleteAutomation(row.id as string, row.name as string, 'OPEN_API_ADDON')
      },
    })
  }

  const goToCreateKey = () => history.push(publicApiPaths.createKey)

  return (
    <div className="main-content addons-view-page">
      <PageHeader title={<IntlMessages id="automations.OPEN_API" />} parent={[{ label: <IntlMessages id="app.integrations" />, to: FrontendUrls.integrations }]} />
      <div className="main-content-body">
        {amIAdmin && status && PAID_ADDON_STATUSES.includes(status) && (
          <HandleSubscribeAutomations
            handleSubscribe={handleSubscribe}
            status={status}
            automationType="OPEN_API"
            formatMessage={formatMessage}
            includedInCurrentPlan={addonDetails?.includedIn?.includes(currentPlan ?? '') || false}
          />
        )}
        <div className="main-content-info">
          <IntlMessages id="addons.openApiInfo" />
        </div>
        <div className="open-api-documentation-card">
          <h2>
            <IntlMessages id="addons.openApi.documentation" />
          </h2>
          <p>
            <IntlMessages id="addons.openApi.documentationInfo" />
          </p>
          <Button size="large" href={DOCUMENTATION_URL} target="_blank" rel="noopener noreferrer">
            <IntlMessages id="addons.openApi.dpenDocumentation" />
          </Button>
          {automations.length > 0 && (
            <Alert
              style={{ marginTop: 16 }}
              type="info"
              showIcon
              message={
                <IntlMessages
                  id="addons.openApi.migrationNote"
                  values={{
                    link: (...chunks) => (
                      <a href={MIGRATION_GUIDE_URL} target="_blank" rel="noopener noreferrer">
                        {chunks}
                      </a>
                    ),
                  }}
                />
              }
            />
          )}
        </div>
        <p>
          <IntlMessages
            id="addons.openApi.feedback"
            values={{
              link: (...chunks) => <a onClick={() => setShowFeedbackForm(true)}>{chunks}</a>,
            }}
          />
        </p>
        <Divider />

        {amIAdmin && (
          <>
            <div>
              <h2>
                <IntlMessages id="publicApi.keys.title" />
              </h2>
              <p>
                <IntlMessages id="publicApi.keys.descriptionAdmin" />
              </p>
              <Button size="large" type="primary" onClick={goToCreateKey} style={{ marginBottom: 16 }}>
                <IntlMessages id="publicApi.keys.create" />
              </Button>
              <ApiKeysTable
                keys={(apiKeys ?? []).map((key) => toTableItem(key, intl))}
                showOwner
                onOpen={(id) => history.push(publicApiPaths.key(id))}
                loading={apiKeysLoading}
                onCreate={goToCreateKey}
              />
            </div>

            <Divider />
            <LegacyApiKeysTable keys={automations} canDelete={amIAdmin} deletingId={deleteAutomationId} onDelete={showConfirmDelete} />

            <Divider />
            <div>
              <h2>
                <IntlMessages id="publicApi.keys.webhooksTitle" />
              </h2>
              <p>
                <IntlMessages id="publicApi.keys.webhooksDescription" />
              </p>
              <Button size="large" onClick={() => history.push(publicApiPaths.webhooks)}>
                <IntlMessages id="publicApi.keys.manageWebhooks" />
              </Button>
            </div>
          </>
        )}

        <Divider />

        <h2>
          <IntlMessages id="addons.openApi.quotasAndRateLimitsTitle" />
        </h2>
        <p>
          <IntlMessages id="addons.openApi.quotasAndRateLimitsInfo" />
        </p>
        <ul>
          <li>
            <IntlMessages id="addons.openApi.limitRequestPerMonth" />
          </li>
          <li>
            <IntlMessages id="addons.openApi.limitRequestPerSecound" />
          </li>
          <li>
            <IntlMessages id="publicApi.limits.idempotency" />
          </li>
        </ul>
      </div>
      <FeedbackModal
        visible={showFeedbackForm}
        onCancel={() => {
          setShowFeedbackForm(false)
        }}
        onSave={sendFeedbackContactForm}
        emailRequired={true}
      />
    </div>
  )
}

export default OpenApiView
